import { useEffect } from 'react';
import { ArrowDownIcon, ArrowRightIcon, ExternalLinkIcon } from 'lucide-react';
import { images, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
import { SundanceCatalog } from '../components/SundanceCatalog';
const TITLE = 'Sundance Spas in Brooklyn, Michigan | Pegasus Spas';
const DESCRIPTION = 'Compare Sundance Spas models by series and seating, then try the seats and jets at Pegasus Spas in Brooklyn, Michigan.';
const CANONICAL = 'https://www.pegasus-spas.com/hot-tubs/sundance-spas';
const MODELS_URL = 'https://pegasus-spas.dealerhottubpages.com#/collections';
const differences = [{
  label: 'SmartTub',
  body: 'Sundance offers connected control, monitoring and alerts through its SmartTub system, so the spa can be checked and adjusted from a phone.'
}, {
  label: 'Fluidix jets',
  body: 'Fluidix is Sundance’s own approach to jet design, intended to create a range of different massage experiences rather than one sensation repeated across the seats.'
}, {
  label: 'Water care',
  body: 'Sundance builds water-care systems and options into its spas with the aim of making everyday ownership more manageable.'
}, {
  label: 'Energy efficiency',
  body: 'Design and insulation choices are made with year-round use in mind, which matters in a climate like ours.'
}, {
  label: 'Thoughtful design',
  body: 'Seating, controls, materials and the small details are where spas differ most, and those are far easier to judge sitting in one than reading about one.'
}];
const relatedLinks = [{
  label: 'Explore Hot Tubs',
  href: routes.hotTubs
}, {
  label: 'See Available Spas',
  href: routes.availableSpas
}, {
  label: 'Explore Financing',
  href: routes.financing
}, {
  label: 'Spa Care & FAQs',
  href: routes.spaCare
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function SundanceSpasPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-2286" data-mp="3dunbb99kamaf">
      {/* Brand opening */}
      <section aria-labelledby="sundance-h1" className="relative w-full" data-id="element-2287" data-mp="3drj19v9f0wbu">
        <div className="relative w-full overflow-hidden min-h-[400px] sm:min-h-[380px] lg:h-[420px]" data-id="element-2288" data-mp="3is6os6yfqv1b">
          <img src={images.sundance} alt="Sundance Spas Cameo hot tub in a landscaped backyard" className="absolute inset-0 h-full w-full object-cover object-center" width={1200} height={900} decoding="async" data-id="element-2289" data-mp="1jh5m7htidhda" />
          <div className="hero-scrim-compact absolute inset-0 sm:hidden" aria-hidden="true" data-id="element-2290" data-mp="1c2v8kj48v4ry" />
          <div className="hero-scrim absolute inset-0 hidden sm:block" aria-hidden="true" data-id="element-2291" data-mp="1764u20838h8n" />
          <span className="pointer-events-none absolute bottom-3 right-3 whitespace-nowrap rounded-[4px] border border-white/15 bg-ink/80 px-2 py-[5px] text-[11px] font-medium leading-none text-white/90 backdrop-blur-[2px] sm:text-[12px]" data-id="element-2292" data-mp="127l5ds240rcl">
            Model: Cameo
          </span>

          <div className="relative mx-auto flex min-h-[400px] max-w-container flex-col justify-center px-5 py-10 sm:min-h-[380px] sm:px-8 lg:h-[420px] lg:min-h-0" data-id="element-2293" data-mp="1pv1mdyjxczo8">
            <p className="text-[14px] font-medium uppercase text-white sm:text-[15px]" data-id="element-2294" data-mp="ego3z97xxcfl">
              Sundance Spas at Pegasus
            </p>
            <h1 id="sundance-h1" className="mt-3 max-w-3xl font-display text-h1-mobile font-semibold text-white lg:text-[48px] lg:leading-[56px]" data-id="element-2295" data-mp="9gjjz97tpzkm">
              Sundance Spas in Brooklyn, Michigan
            </h1>
            <p className="mt-4 max-w-2xl text-body text-white sm:text-body-lg" data-id="element-2296" data-mp="1odm990s9c9ig">
              Find a Sundance spa that feels right for you. Compare models by series and seating
              below, then come try the seats and jets in our Brooklyn showroom.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2297" data-mp="2h31ibl0d3sbq">
              <a href="#sundance-models" className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2298" data-mp="2m0bj1wt7bqev">
                Compare Sundance Models
              </a>
              <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn border-2 border-white bg-white/10 px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-2299" data-mp="vnm0gqumycab">
                Visit the Brooklyn Showroom
              </SiteLink>
            </div>
          </div>
        </div>
      </section>

      {/* What makes Sundance different */}
      <section aria-labelledby="difference-heading" className="w-full border-y border-ink/10 bg-water" data-id="element-2300" data-mp="3jvxdl20vxtre">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2301" data-mp="2w5jif2n07esl">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-2302" data-mp="2r7hxsd9xj7f0">
            <div className="lg:col-span-5" data-id="element-2303" data-mp="34xv64gqqgo4v">
              <h2 id="difference-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2304" data-mp="34ywluyip1ysx">
                What Makes Sundance Different
              </h2>
              <p className="mt-6 border-l-2 border-pegasus-magenta pl-5 font-display text-[23px] font-semibold leading-9 text-ink sm:text-[26px]" data-id="element-2305" data-mp="28fyue5bztrcg">
                The feature list matters. How it feels when you sit in the spa matters more.
              </p>
              <SiteLink to={routes.location} className="mt-7 inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-blue px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-ink" data-id="element-2306" data-mp="gcctq23dygd6">
                Compare Them at Pegasus
              </SiteLink>
            </div>

            <div className="lg:col-span-7" data-id="element-2307" data-mp="3tk62robgjigs">
              <dl data-id="element-2308" data-mp="3tky0b4om7q9p">
                {differences.map(item => <div key={item.label} className="border-t border-ink/15 py-6 first:border-t-0 first:pt-0 sm:grid sm:grid-cols-12 sm:gap-6" data-id="element-2309" data-mp="2dd34owbbred">
                    <dt className="font-display text-[21px] font-semibold leading-8 text-ink sm:col-span-4" data-id="element-2310" data-mp="1dsi8k0ntw41o">
                      {item.label}
                    </dt>
                    <dd className="mt-1.5 text-[16.5px] leading-7 text-ink/75 sm:col-span-8 sm:mt-0" data-id="element-2311" data-mp="33afdir37wins">
                      {item.body}
                    </dd>
                  </div>)}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Official model information */}
      <section aria-labelledby="models-heading" className="w-full bg-white" data-id="element-2312" data-mp="2omkkf9lasca6">
        <div className="mx-auto max-w-container px-5 pt-12 sm:px-8 lg:pt-14" data-id="element-2313" data-mp="2jmg5ho01fpm2">
          <div className="max-w-3xl" data-id="element-2314" data-mp="2jkd5cb568g3m">
            <p className="text-[14px] font-bold uppercase tracking-[0.02em] text-pegasus-blue" data-id="element-2315" data-mp="2fkusm2ni7bbv">
              Sundance Models &amp; Features
            </p>
            <h2 id="models-heading" className="mt-2 font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-2316" data-mp="2anumxsukgala">
              Explore Sundance Models and Features
            </h2>
          </div>
        </div>

        <div className="mx-auto max-w-container px-5 pb-12 pt-6 sm:px-8 lg:pb-16" data-id="element-2317" data-mp="3wv6qzwbywj2">
          <SundanceCatalog data-id="element-2318" data-mp="38wn25j2h6ovk" />


        </div>
      </section>

      {/* Showroom close */}
      <section aria-labelledby="close-heading" className="w-full bg-water" data-id="element-2319" data-mp="3duybztsrszds">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-2320" data-mp="2z627g6pxa4hx">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-2321" data-mp="2z3y5vvfhxhkt">
            <div className="lg:col-span-7" data-id="element-2322" data-mp="342smqrtwkw2a">
              <p className="text-[14px] font-bold uppercase tracking-[0.02em] text-pine" data-id="element-2323" data-mp="2q4krqpgfg4tr">
                Visit Us
              </p>
              <h2 id="close-heading" className="mt-2 font-display text-h2-mobile font-semibold text-ink lg:text-[34px] lg:leading-[42px]" data-id="element-2324" data-mp="2v5s30yhs3m56">
                Find Your Favorite Seat. Feel the Jets.
              </h2>
              <p className="mt-4 max-w-2xl text-body text-ink/80 sm:text-body-lg" data-id="element-2325" data-mp="ef1sc3uzft2v">
                We have 10–12 showroom models to sit in and compare, plus three operating spas
                you can actually try. Feel the difference in the seats, jets and controls, then
                find the spa that fits your space, indoors or out.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-2326" data-mp="1yvj2iaie6mte">
                <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-2327" data-mp="2dn9zoceeq8tr">
                  Come Try a Spa
                </SiteLink>
                <SiteLink to={routes.availableSpas} className="inline-flex min-h-[48px] items-center rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-2328" data-mp="2inxu781uh7sq">
                  See Available Spas
                </SiteLink>
              </div>
            </div>

            <div className="lg:col-span-5" data-id="element-2329" data-mp="13incipbd0vk8">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-card" data-id="element-2330" data-mp="13jos973bm68a">
                <img src={images.pathHotTubs} alt="Hot tub set into a backyard deck with the cover open" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-2331" data-mp="yjar8904zwjr" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-2332" data-mp="1h9s89gblx3pa">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-2333" data-mp="1cijuk838uw6b">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-2334" data-mp="18hq6nepsxhdf">
            {relatedLinks.map(link => <li key={link.label} data-id="element-2335" data-mp="250o20izmi8z6">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-2336" data-mp="251qvmyobypis">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2337" data-mp="3rv144r3jas9y" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}